import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useSelector } from 'react-redux';
import type { RootState } from '../../store';
import { applicationDefaultTabKey, dashboardRoleKind } from '../../utils/roleAwareEntityNavigation';
import { Alert, Button, Spin } from 'antd';
import { ArrowLeftOutlined } from '@ant-design/icons';
import { PageLayout } from '../../ui';
import { useGetApplicationByIdQuery } from '../../store/api/applicationsApi';
import ApplicationDetails, { formatApplicationPageTitle } from '../../components/applications/ApplicationDetails';
import ApplicationTaskModalHost, { ApplicationTaskModalAction } from '../../components/applications/ApplicationTaskModalHost';

const APPLICATION_DETAILS_TAB_KEYS = new Set(['main', 'tasks', 'finance', 'cargo', 'routes', 'penalties']);

const ApplicationViewPage: React.FC = () => {
  const navigate = useNavigate();
  const { id: idParam } = useParams<{ id: string }>();
  const [searchParams] = useSearchParams();
  const authUser = useSelector((state: RootState) => state.auth.user);

  const applicationId = useMemo(() => {
    if (idParam == null || idParam === '') return NaN;
    const n = parseInt(idParam, 10);
    return Number.isFinite(n) ? n : NaN;
  }, [idParam]);

  const skip = Number.isNaN(applicationId) || applicationId < 1;

  const {
    data: application,
    isLoading,
    isError,
    error,
    refetch: refetchApplication
  } = useGetApplicationByIdQuery(applicationId, { skip });

  const [focusSection, setFocusSection] = useState<'finance' | null>(null);
  const [openCreateInvoiceOnShow, setOpenCreateInvoiceOnShow] = useState(false);
  const [taskModalAction, setTaskModalAction] = useState<ApplicationTaskModalAction | null>(null);
  const [taskModalInvoiceId, setTaskModalInvoiceId] = useState<number | null>(null);

  const tab = searchParams.get('tab');
  const action = searchParams.get('action');
  const invoiceId = searchParams.get('invoiceId');

  const initialDetailsTab = useMemo(() => {
    if (tab && APPLICATION_DETAILS_TAB_KEYS.has(tab)) return tab;
    return applicationDefaultTabKey(dashboardRoleKind(authUser?.role?.name));
  }, [tab, authUser?.role?.name]);

  useEffect(() => {
    if (skip) return;

    if (!tab && !action) return;

    const normalizedAction = (action || '').trim() as ApplicationTaskModalAction | 'create-invoice';

    if (tab === 'finance') {
      setFocusSection('finance');
      if (normalizedAction === 'create-invoice') {
        setOpenCreateInvoiceOnShow(true);
      }
      if (normalizedAction === 'confirm-invoice-sent') {
        setTaskModalAction('confirm-invoice-sent');
        setTaskModalInvoiceId(invoiceId ? Number(invoiceId) : null);
      }
      navigate(`/applications/${applicationId}`, { replace: true });
      return;
    }

    const supportedActions: ApplicationTaskModalAction[] = [
      'upload-loading-docs',
      'upload-unloading-docs',
      'upload-upd',
      'confirm-upd-sent',
      'confirm-invoice-sent',
    ];
    if (supportedActions.includes(normalizedAction as ApplicationTaskModalAction)) {
      setTaskModalAction(normalizedAction as ApplicationTaskModalAction);
      setTaskModalInvoiceId(invoiceId ? Number(invoiceId) : null);
      navigate(`/applications/${applicationId}`, { replace: true });
    }
  }, [action, applicationId, invoiceId, navigate, skip, tab]);

  if (skip) {
    return (
      <PageLayout title="Заявка" titleLevel={2}>
        <Alert type="error" message="Некорректная ссылка" showIcon />
      </PageLayout>
    );
  }

  if (isLoading && !application) {
    return (
      <PageLayout title="Заявка" titleLevel={2}>
        <div style={{ textAlign: 'center', padding: 48 }}>
          <Spin size="large" />
        </div>
      </PageLayout>
    );
  }

  if (isError || !application) {
    const msg =
      error && 'data' in error && (error as { data?: { error?: string } }).data?.error
        ? String((error as { data?: { error?: string } }).data?.error)
        : 'Не удалось загрузить заявку. Возможно, запись удалена или недоступна.';
    return (
      <PageLayout title="Заявка" titleLevel={2}>
        <Alert type="error" message={msg} showIcon />
        <div style={{ marginTop: 16 }}>
          <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/applications')}>
            К списку заявок
          </Button>
        </div>
      </PageLayout>
    );
  }

  return (
    <PageLayout
      titleLevel={2}
      title={formatApplicationPageTitle(application)}
      actions={
        <Button type="default" icon={<ArrowLeftOutlined />} onClick={() => navigate('/applications')}>
          К списку
        </Button>
      }
    >
      <ApplicationDetails
        application={application}
        focusSection={focusSection}
        initialTabKey={initialDetailsTab}
        openCreateInvoiceOnShow={openCreateInvoiceOnShow}
        onCreateInvoiceIntentHandled={() => {
          setOpenCreateInvoiceOnShow(false);
        }}
        onRefresh={async () => {
          await refetchApplication();
        }}
      />
      <ApplicationTaskModalHost
        open={taskModalAction != null}
        applicationId={applicationId}
        action={taskModalAction}
        invoiceId={taskModalInvoiceId}
        onClose={() => {
          setTaskModalAction(null);
          setTaskModalInvoiceId(null);
        }}
        onSuccess={() => {
          void refetchApplication();
        }}
      />
    </PageLayout>
  );
};

export default ApplicationViewPage;
